import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Menu, ServerCog } from 'lucide-react';
import { type ReactNode } from 'react';

import { useSair } from '@/modulos/sessao/consultas';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { AlternadorTema } from './AlternadorTema';
import { Sino } from './Sino';
import { InterruptorDeHistorias } from '@/historias/mesa';
import { Botao } from './Botao';
import { BuscaDaBarra } from './BuscaDaBarra';
import { SimboloContrato } from './Marca';
import { CAMINHO_DO_LOGIN } from './Casca';

/** Pessoa sem papel cadastrado. Nao e um papel: e a ausencia de um. */
const SEM_PAPEL = 'Sem papel cadastrado';

/**
 * Barra superior (v2.1, p.34): SEM FUNDO, 56 px, sobre o fundo da aplicacao.
 * A trilha a esquerda; a busca, os avisos, o tema e a pessoa a direita. O
 * logotipo e a assinatura da Atenas moram no topo do menu.
 *
 * A BUSCA FAZ O QUE O NOME DIZ: equipamento e documento da biblioteca
 * técnica, num menu de resultados colado ao campo (`BuscaDaBarra`). O Enter
 * sem escolher continua indo para a lista de equipamentos com o `?termo=`.
 * Não é ainda a busca global da p.33 (pessoa e página, com Ctrl K).
 *
 * EM TELA ESTREITA A BARRA NAO EMPURRA A PAGINA (p.18):
 *  - abaixo de 1024 px (menu em gaveta), o tema e "Sair do sistema" vao para
 *    o pe da gaveta do menu, e o ☰ e o simbolo da Contrato entram aqui;
 *  - abaixo de 768, a pessoa e a trilha tambem saem, e a margem cai para 16;
 *  - a busca nunca fica mais estreita que 160 px.
 */
export function BarraSuperior({
  aoAbrirMenu,
  menuAberto = false,
  trilha = null,
  semBusca = false,
}: {
  readonly aoAbrirMenu?: () => void;
  /**
   * Sem a busca de equipamento e documento: a pessoa de posto parceiro (0132)
   * não lê a frota nem a biblioteca, e o servidor recusaria cada letra.
   */
  readonly semBusca?: boolean;
  readonly menuAberto?: boolean;
  /** A trilha de onde a pessoa esta (a `Trilha` do menu, que a casca entrega). */
  readonly trilha?: ReactNode;
} = {}) {
  return (
    <header className="flex h-[var(--barra-superior)] shrink-0 items-center gap-4 pr-8 pl-6 max-[1280px]:pr-6 max-[1024px]:gap-3 max-[1024px]:pl-6 max-[768px]:gap-2 max-[768px]:px-4">
      {/* Abaixo de 1024 px o menu vira gaveta (p.18), aberta por aqui. */}
      <button
        type="button"
        aria-label="Abrir o menu"
        title="Abrir o menu"
        aria-expanded={menuAberto}
        onClick={aoAbrirMenu}
        className="botao botao-fantasma botao-p botao-icone hidden shrink-0 max-[1024px]:inline-flex"
      >
        <Menu aria-hidden="true" className="size-[var(--icone-controle)]" />
      </button>
      <SimboloContrato className="hidden max-[1024px]:block" />

      {/* A trilha (p.34): grupo, modulo e tela. A ultima e onde se esta. */}
      {trilha}

      <div className="ml-auto flex min-w-0 items-center gap-2">
        {semBusca ? null : <BuscaDaBarra />}

        {/* Homologação · Histórias (p.44): só existe em homologação. */}
        <span className="contents max-[1280px]:hidden">
          <InterruptorDeHistorias />
        </span>

        {/* Os avisos (p.33): o sino, com a contagem em vermelho. */}
        <Sino />

        {/* Abaixo de 1024 px, no pe da gaveta do menu (ver `Navegacao`). */}
        <span className="contents max-[1024px]:hidden">
          <AlternadorTema />
        </span>

        <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-borda max-[768px]:hidden" />
        <ChipDaPessoa className="max-[768px]:hidden" />

        <BotaoDeAmbiente className="max-[1024px]:hidden" />
        <BotaoSair className="max-[1024px]:hidden" />
      </div>
    </header>
  );
}

/**
 * QUEM ESTA DENTRO, E DE QUE EMPRESA. A empresa aparece porque ela e o recorte
 * de TUDO o que a tela mostra: a mesma consulta, com outra credencial, devolve
 * outra frota. Quem confere um numero precisa saber de que empresa ele e sem
 * abrir outra tela.
 *
 * CLICAVEL, e nao decorativo: o nome leva a `/perfil`, onde esta a descricao
 * completa da persona de quem entrou — a mesma que morava na tela de login,
 * visivel sem sessao. O nome na barra e o lugar onde todo sistema poe isso,
 * entao nao precisou de item novo no menu.
 *
 * `Link` e nao `Botao`: e navegacao, e navegacao tem de abrir em outra aba com
 * o meio do mouse.
 *
 * O ANEL DE FOCO NAO E DECLARADO AQUI. Ele e global (`:focus-visible` em
 * global.css, regra 8 do manual) e vale para link tambem. A unica coisa
 * declarada e o DESLOCAMENTO negativo, e pelo motivo que o proprio global.css
 * escreve: a regra geral de link usa `outline-offset: 0` porque link nao tem
 * borda propria — este tem, e sem o deslocamento apareceriam duas linhas
 * concentricas em volta da caixa.
 *
 * Mora na barra de 768 px para cima e no pe da gaveta do menu abaixo disso
 * (R01). Encolhe cortando o nome e o papel, nunca empurrando a pagina.
 */
export function ChipDaPessoa({ className = '' }: { readonly className?: string }) {
  const sessao = useSessaoEmMemoria();
  const papel = sessao === null ? '' : (sessao.perfilNome ?? sessao.papeis[0]?.nome ?? SEM_PAPEL);
  return (
    <Link
      to="/perfil"
      className={`flex min-w-0 items-center gap-2 rounded-pill py-1 pr-3 pl-1 transition-colors hover:bg-rebaixada ${className}`}
      title="Ver o seu perfil: o papel que você tem e o que ele alcança"
    >
      {/* Avatar (p.24): iniciais em circulo; a pessoa logada em preto. */}
      <span aria-hidden="true" className="txt-legenda flex size-8 shrink-0 items-center justify-center rounded-pill bg-tinta font-semibold text-tela">
        {iniciais(sessao?.nome ?? '')}
      </span>
      <span className="flex min-w-0 flex-col">
        {/* Sem a sessao em memoria ainda (p.32): barra no lugar do nome, e nao
            "Carregando…" escrito onde vai um nome. */}
        {sessao ? (
          <span className="txt-legenda truncate font-semibold text-tinta">{sessao.nome}</span>
        ) : (
          <span aria-hidden="true" className="esqueleto my-1 block h-3 w-24" />
        )}
        {/* O PAPEL QUE RECORTA O MENU, e nao o primeiro da lista: a mesma
            escolha da navegacao, para os dois nunca discordarem em silencio. */}
        <span className="txt-legenda truncate font-normal text-tinta-secundaria">{papel}</span>
      </span>
    </Link>
  );
}

/** As iniciais do avatar: a primeira letra do primeiro e do ultimo nome. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return `${primeira}${ultima}`.toLocaleUpperCase('pt-BR');
}

/**
 * A TROCA DE AMBIENTE da conta de suporte (0134, decisão 62): quem administra
 * a plataforma e tem a conta vinculada nesta empresa vai para a administração
 * sem entrar de novo — a entrada com a senha unificada abriu as duas sessões.
 * Se a da plataforma tiver vencido (ela dura menos), a administração mostra a
 * entrada dela. `server-cog` é o ícone da administração da plataforma (M37).
 */
export function BotaoDeAmbiente({ className = '' }: { readonly className?: string }) {
  const sessao = useSessaoEmMemoria();
  const cliente = useQueryClient();
  if (sessao?.contaDeSuporte?.vinculada !== true) return null;
  return (
    <Link
      to="/plataforma"
      className={`botao botao-fantasma botao-p shrink-0 ${className}`}
      title="Ir para a administração da plataforma, sem entrar de novo"
      onClick={() => {
        void cliente.invalidateQueries({ queryKey: ['plataforma'] });
      }}
    >
      <ServerCog aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
      Administração
    </Link>
  );
}

/**
 * Sair do sistema (p.19: `log-out`). Na barra, o botao redondo so com o icone,
 * que leva nome e dica — "Sair do sistema", verbo e objeto. No pe da gaveta do
 * menu, abaixo de 1024 px, o mesmo botao com o rotulo escrito.
 *
 * Fantasma, e nao laranja: a acao primaria e da pagina, nao da casca (p.21).
 */
export function BotaoSair({ className = '', forma = 'icone' }: { readonly className?: string; readonly forma?: 'icone' | 'texto' }) {
  const navegar = useNavigate();
  const sessao = useSessaoEmMemoria();
  const sair = useSair();
  const dica =
    sessao === null ? 'Não há sessão aberta.' : `Encerra a sessão de ${sessao.nome} e volta para a tela de entrada.`;
  return (
    <Botao
      nivel="fantasma"
      {...(forma === 'icone' ? { tamanho: 'p' as const, icone: true, 'aria-label': 'Sair do sistema' } : {})}
      className={`shrink-0 ${className}`}
      onClick={() => {
        sair.mutate(undefined, {
          onSuccess: () => {
            void navegar({ to: CAMINHO_DO_LOGIN });
          },
        });
      }}
      disabled={sessao === null || sair.isPending}
      title={forma === 'icone' ? `Sair do sistema. ${dica}` : dica}
    >
      <LogOut aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
      {forma === 'icone' ? null : 'Sair do sistema'}
    </Botao>
  );
}
